import { useState } from "react";
import { ChevronLeft, ChevronRight, Landmark, MapPin, Pencil, Plus, Sparkles, X } from "lucide-react";
import { GmapsProvider } from "../builder/gmaps";
import { MapPanel } from "../builder/MapPanel";
import { GoldImage } from "../GoldImage";
import { stopCity, svcName } from "./utils";

/* «Il tuo viaggio personalizzato»: tappe reali dalla proposta AI, cliccabili,
 * con programma, modifica (aggiungi/rimuovi tappa), richiesta contestuale
 * all'AI e mappa riusata dal builder (marker oro + marcatori alloggi). */
export const ItineraryPanel = ({ proposal, trip, catalog, activeIdx, setActiveIdx, editMode, setEditMode, showMap, setShowMap, onAddStop, onRemoveStop, onAskAi, tc, lang }) => {
    const [addCity, setAddCity] = useState("");
    const stops = proposal?.stops || [];
    const dests = catalog?.italy_destinations || [];

    if (!proposal) {
        return (
            <section className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-amber-500/20 p-10 text-center" data-testid="concierge-proposal-empty">
                <MapPin size={26} className="text-amber-400/70" />
                <p className="mt-4 max-w-md text-sm leading-relaxed text-stone-400">{tc.emptyItinerary}</p>
            </section>
        );
    }

    const idx = Math.min(activeIdx, Math.max(stops.length - 1, 0));
    const active = stops[idx];
    const activeCity = active ? stopCity(active, lang) : "";
    const taken = new Set(stops.map((s) => stopCity(s, "it").toLowerCase()));
    const available = dests.filter((d) => !taken.has(d.name.it.toLowerCase()));
    const imgFor = (s) => {
        const c = stopCity(s, lang).toLowerCase();
        const d = dests.find((x) => x.name.it.toLowerCase() === c || x.name.en.toLowerCase() === c);
        return d?.image || "";
    };
    const totalNights = stops.reduce((a, s) => a + (Number(s.nights) || 0), 0);
    const linked = active
        ? [...(trip?.stays || []), ...(trip?.experiences || []), ...(trip?.transfers || [])].filter((it) => {
            const m = it.meta || {};
            const hay = [m.locality, m.region, m.address, m.destination, it.city_id].filter(Boolean).join(" ").toLowerCase();
            return hay && (hay.includes(activeCity.toLowerCase()) || (it.city_id && activeCity.toLowerCase().includes(it.city_id.replace(/-/g, " "))));
        })
        : [];
    const mapStops = stops.filter((s) => s.lat != null && s.lng != null).map((s, i) => ({ id: `stop-${i}`, name: stopCity(s, lang), lat: s.lat, lng: s.lng }));
    const stayExtras = [
        ...(trip?.stays || []).filter((s) => s.lat != null).map((s) => ({ id: `stay-${s.ref_id}`, name: svcName(s, lang), lat: s.lat, lng: s.lng, kind: "stay" })),
        ...(trip?.restaurants || []).filter((s) => s.lat != null).map((s) => ({ id: `rst-${s.ref_id}`, name: svcName(s, lang), lat: s.lat, lng: s.lng })),
    ];

    return (
        <section className="rounded-2xl border border-amber-500/25 bg-[#0b0b0c]/90 p-5 sm:p-7" data-testid="concierge-proposal">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-400/90 font-semibold">
                        <Landmark size={13} /> {tc.journeyTitle}
                    </p>
                    <h2 className="mt-2 font-display text-xl text-amber-50 sm:text-2xl" data-testid="proposal-title">{proposal.title}</h2>
                    <p className="mt-1 text-[11px] text-stone-400">
                        {totalNights > 0 && <>{totalNights} {tc.nights} · </>}{stops.map((s) => stopCity(s, lang)).join(" · ")}
                        {proposal.participants ? <> · {tc.participants}: {proposal.participants}</> : null}
                    </p>
                </div>
                <button onClick={() => setEditMode(!editMode)} data-testid="edit-itinerary-btn"
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${editMode ? "border-amber-300 bg-amber-400/15 text-amber-200" : "border-amber-500/40 text-amber-200 hover:bg-amber-400/10"}`}>
                    <Pencil size={12} /> {editMode ? tc.doneEdit : tc.editItinerary}
                </button>
            </div>

            {/* Tappe cliccabili */}
            <div className="mt-5 flex gap-2 overflow-x-auto pb-1" data-testid="proposal-stops">
                {stops.map((s, i) => (
                    <div key={`${s.order}-${i}`} className="relative shrink-0">
                        <button onClick={() => setActiveIdx(i)} data-testid={`stop-tab-${s.order || i + 1}`}
                            className={`flex w-24 flex-col items-center rounded-xl border px-2 py-2.5 transition-all ${i === idx ? "border-amber-300 bg-amber-400/15 shadow-[0_0_18px_rgba(212,175,55,0.15)]" : "border-amber-500/20 hover:border-amber-400/40"}`}>
                            <span className={`flex h-7 w-7 items-center justify-center rounded-full border font-mono-accent text-[11px] font-bold ${i === idx ? "border-amber-300 bg-amber-300 text-black" : "border-amber-400/60 text-amber-300"}`}>{s.order || i + 1}</span>
                            <span className="mt-1.5 max-w-full truncate text-[11px] font-semibold text-amber-50">{stopCity(s, lang)}</span>
                            <span className="text-[9px] text-stone-500">{s.nights} {tc.nights}</span>
                        </button>
                        {editMode && stops.length > 1 && (
                            <button onClick={() => onRemoveStop(s.order || i + 1)} data-testid={`stop-remove-${s.order || i + 1}`} aria-label={tc.removeStop}
                                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-red-400/60 bg-[#1a0f0f] text-red-300 transition-colors hover:bg-red-400/20">
                                <X size={10} />
                            </button>
                        )}
                    </div>
                ))}
                {editMode && (
                    <div className="flex shrink-0 items-center gap-1.5">
                        <select value={addCity} onChange={(e) => setAddCity(e.target.value)} data-testid="add-destination-select"
                            className="input-lux !w-40 !py-2 text-[11px]">
                            <option value="">{tc.chooseCity}</option>
                            {available.map((d) => <option key={d.id} value={d.id}>{d.name[lang] || d.name.it}</option>)}
                        </select>
                        <button onClick={() => { if (addCity) { onAddStop(addCity); setAddCity(""); } }} disabled={!addCity} data-testid="add-destination-btn"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-black transition-colors hover:bg-amber-300 disabled:opacity-40" aria-label={tc.addDestination}>
                            <Plus size={15} />
                        </button>
                    </div>
                )}
            </div>

            {/* Dettaglio tappa attiva + mappa */}
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
                {active && (
                    <div className="overflow-hidden rounded-xl border border-amber-500/20" data-testid="stop-detail">
                        <div className="relative h-36">
                            <GoldImage src={imgFor(active)} alt={activeCity} className="absolute inset-0 h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                            <p className="absolute bottom-2.5 left-3.5 font-display text-base text-amber-50">
                                {tc.day} {active.order || idx + 1} · {activeCity}
                            </p>
                        </div>
                        <div className="p-4">
                            <p className="text-[11px] text-amber-500/80">
                                {active.nights} {tc.nights}{active.dates ? ` · ${active.dates}` : ""}
                            </p>
                            <p className="mt-2 text-xs leading-relaxed text-stone-300">{active.notes || tc.programDefault}</p>
                            {linked.length > 0 && (
                                <p className="mt-3 border-t border-amber-500/10 pt-2.5 text-[10px] uppercase tracking-[0.15em] text-stone-500" data-testid="stop-linked">
                                    {tc.linkedServices}: <b className="text-amber-300">{linked.length}</b>
                                </p>
                            )}
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <button onClick={() => onAskAi(tc.askAiStop.replace("{city}", activeCity))} data-testid="ask-ai-stop-btn"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200 transition-colors hover:bg-amber-400/10">
                                    <Sparkles size={11} /> {tc.askAi}
                                </button>
                                <button onClick={() => setShowMap(!showMap)} data-testid="map-toggle"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/25 px-3.5 py-1.5 text-[10px] uppercase tracking-wider text-stone-300 transition-colors hover:text-amber-200 lg:hidden">
                                    <MapPin size={11} /> {showMap ? tc.hideMap : tc.viewMap}
                                </button>
                                <span className="ms-auto flex gap-1.5">
                                    <button onClick={() => setActiveIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="stop-prev"
                                        className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-500/30 text-amber-200 transition-colors hover:bg-amber-400/10 disabled:opacity-30" aria-label="prev">
                                        <ChevronLeft size={14} />
                                    </button>
                                    <button onClick={() => setActiveIdx(Math.min(stops.length - 1, idx + 1))} disabled={idx >= stops.length - 1} data-testid="stop-next"
                                        className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-500/30 text-amber-200 transition-colors hover:bg-amber-400/10 disabled:opacity-30" aria-label="next">
                                        <ChevronRight size={14} />
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`${showMap ? "" : "hidden"} lg:block`}>
                    {mapStops.length > 0 && (
                        <GmapsProvider>
                            <div data-testid="concierge-map">
                                <MapPanel stops={mapStops} extras={stayExtras} height="100%" routePath={mapStops.map((s) => ({ lat: s.lat, lng: s.lng }))} />
                            </div>
                        </GmapsProvider>
                    )}
                </div>
            </div>

            {proposal.warnings?.length > 0 && (
                <div className="mt-5 rounded-lg border border-amber-500/20 bg-amber-400/5 p-3.5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500/80 font-semibold">{tc.warningsTitle}</p>
                    <ul className="mt-1.5 space-y-1 text-[11px] text-stone-400">
                        {proposal.warnings.map((w, i) => <li key={i}>· {w}</li>)}
                    </ul>
                </div>
            )}
            <p className="mt-4 text-[11px] leading-relaxed text-stone-500">{tc.disclaimer}</p>
        </section>
    );
};
